import { Component, EventEmitter, Input, OnInit, OnDestroy, Output, SimpleChanges, ElementRef, ViewChild, AfterViewInit } from "@angular/core";
import countries, { validatePhoneByType } from "../shared/json/countries";

@Component({
  selector: "app-iti-tel-input",
  templateUrl: "./iti-tel-input.component.html",
  styleUrls: ["./iti-tel-input.component.css"],
})
export class ItiTelInputComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() phone_without_code: number;
  @Input() dial_code: string;
  @Output() phoneEvent = new EventEmitter<any>();
  @ViewChild('telInput') telInputRef: ElementRef;
  
  phoneValid: any;
  telInputObj: any;
  phonePlaceholder: string = '';  // Initialize with empty string to avoid undefined
  phoneType: "mobile" | "landline" | "auto" = "auto";
  detectedPhoneType: "mobile" | "landline" | "unknown" = "unknown";
  phoneTypeOptions = [
    { value: "auto", label: "Auto-detect" },
    { value: "mobile", label: "Mobile" },
    { value: "landline", label: "Landline" },
  ];
  phoneError: string;
  uniqueId: string;
  telInputOptions: any;

  constructor() {
    // Generate unique ID for each instance to avoid conflicts
    this.uniqueId = 'tel-input-' + Math.random().toString(36).substr(2, 9);
    
    // Configure tel input options for multiple instances
    // Note: ng2-tel-input v2.3.0 passes these options directly to intl-tel-input
    this.telInputOptions = {
      initialCountry: 'eg',
      separateDialCode: true,
      preferredCountries: ['eg', 'sa', 'ae', 'kw', 'qa', 'bh', 'om'],
      utilsScript: 'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.8/js/utils.js',
      autoPlaceholder: 'aggressive',
      allowDropdown: true,
      dropdownContainer: document.body,
      formatOnDisplay: true
    };
  }

  ngOnInit() {
    // Initialize placeholder with default country (Egypt)
    this.phonePlaceholder = "Enter phone number";
  }

  ngAfterViewInit() {
    // Add custom search functionality after view initialization
    this.addSearchFunctionality();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.phone_without_code && this.telInputObj) {
      this.setPhoneWithCountry(this.telInputObj, this.phone_without_code, this.dial_code);
    }
  }

  private addSearchFunctionality(): void {
    // Add search functionality after a delay to ensure dropdown is initialized
    setTimeout(() => {
      if (this.telInputObj && this.telInputObj.flagsContainer) {
        // Listen for dropdown open events
        const flagButton = this.telInputObj.flagsContainer.querySelector('.iti__selected-flag');
        if (flagButton) {
          flagButton.addEventListener('click', () => {
            setTimeout(() => {
              this.injectSearchBox();
            }, 50);
          });
        }
      }
    }, 500);
  }

  private injectSearchBox(): void {
    // Find the country list for this specific instance
    const dropdowns = document.querySelectorAll('.iti__country-list');
    const activeDropdown = Array.from(dropdowns).find(dropdown => 
      (dropdown as HTMLElement).style.display !== 'none' && 
      (dropdown as HTMLElement).offsetHeight > 0
    ) as HTMLElement;

    if (activeDropdown && !activeDropdown.querySelector('.iti-search-box')) {
      // Create a container div for the search box to prevent scrolling issues
      const searchContainer = document.createElement('div');
      searchContainer.className = 'iti-search-container';
      searchContainer.style.cssText = `
        position: sticky;
        top: 0;
        background: white;
        z-index: 10;
        border-bottom: 1px solid #ddd;
      `;

      // Create search input
      const searchBox = document.createElement('input');
      searchBox.type = 'text';
      searchBox.className = 'iti-search-box';
      searchBox.placeholder = 'Search countries...';
      searchBox.style.cssText = `
        width: 100%;
        padding: 8px 10px;
        border: 1px solid #ddd;
        border-radius: 4px;
        box-sizing: border-box;
        font-size: 14px;
      `;

      // Add search box to container
      searchContainer.appendChild(searchBox);

      // Insert container at the top of the dropdown
      activeDropdown.insertBefore(searchContainer, activeDropdown.firstChild);

      // Add search functionality
      searchBox.addEventListener('input', (e) => {
        e.stopPropagation();
        e.preventDefault();
        
        const searchTerm = (e.target as HTMLInputElement).value.toLowerCase();
        const countryItems = activeDropdown.querySelectorAll('.iti__country');
        
        countryItems.forEach((item: HTMLElement) => {
          const countryNameElement = item.querySelector('.iti__country-name');
          const countryName = countryNameElement ? (countryNameElement.textContent || '').toLowerCase() : '';
          const dialCodeElement = item.querySelector('.iti__dial-code');
          const dialCode = dialCodeElement ? (dialCodeElement.textContent || '') : '';
          
          if (countryName.includes(searchTerm) || dialCode.includes(searchTerm)) {
            item.style.display = '';
          } else {
            item.style.display = 'none';
          }
        });
      });

      // Prevent dropdown from closing and scrolling when interacting with search box
      searchBox.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
      });

      searchBox.addEventListener('keydown', (e) => {
        e.stopPropagation();
        // Allow typing but prevent dropdown from closing
        if (e.key === 'Escape') {
          e.preventDefault();
        }
      });

      searchBox.addEventListener('mousedown', (e) => {
        e.stopPropagation();
        e.preventDefault();
        searchBox.focus();
      });

      searchBox.addEventListener('focus', (e) => {
        e.stopPropagation();
      });

      // Focus on search box after a small delay to ensure proper initialization
      setTimeout(() => {
        searchBox.focus();
      }, 100);
    }
  }

  private setPhoneWithCountry(telInputObj: any, phoneNumber: number, dial_code?: string): void {
    if (!telInputObj || !phoneNumber) return;

    // Sort countries by dial code length (longest first) to match correctly
    const sortedCountries = [...countries].sort(
      (a, b) => b.dial_code.length - a.dial_code.length
    );

    // Find country by matching dial code in phone number
    for (const country of sortedCountries) {
      if (dial_code == country.dial_code) {
        const iso = country.code.toLowerCase();
        telInputObj.setCountry(iso);
        telInputObj.telInput.value = phoneNumber;
        this.sendPhone();
        return;
      }
    }

    // Default to Egypt if no match
    telInputObj.setCountry("eg");
    this.onCountryChange({ dialCode: '20' });
    telInputObj.telInput.value = phoneNumber;
  }

  telInputObject(obj: any) {
    this.telInputObj = obj;
    console.log('this.telInputObj', this.telInputObj);
    
    // Ensure proper initialization for this instance
    setTimeout(() => {
      if (this.telInputObj) {
        // Set default country
        this.telInputObj.setCountry("eg");
        
        // Set initial placeholder for Egypt if no placeholder is set
        if (!this.phonePlaceholder || this.phonePlaceholder === 'Enter phone number') {
          const egyptCountry = countries.find(c => c.code === 'EG');
          if (egyptCountry) {
            this.phonePlaceholder = `(+20) ${egyptCountry.phone_placeholder}`;
          }
        }
        
        // Initialize with existing phone data if available
        if (this.phone_without_code && this.dial_code) {
          this.setPhoneWithCountry(this.telInputObj, this.phone_without_code, this.dial_code);
        }
        
        // Re-add search functionality after object is initialized
        this.addSearchFunctionality();
      }
    }, 100);
  }

  getNumber(number: string) {
    if (this.telInputObj) {
      // apply our custom validation with phone type detection
      if (number && number.length > 2) {
        this.validatePhone();
      } else {
        // Reset detected type when number is too short
        this.detectedPhoneType = "unknown";
        this.phoneValid = "";
      }
    }
  }

  onCountryChange(countryData: any) {
    const country = countries.find(
      (c) => c.dial_code === `+${countryData.dialCode}`
    );
    this.phonePlaceholder = country
      ? `${country.phone_placeholder}`
      : "";
    this.telInputObj.telInput.value = null;
  }

  validatePhone(): void {
    // Guard check to ensure telInputObj is initialized
    if (!this.telInputObj || !this.telInputObj.telInput) {
      this.phoneValid = true;
      this.phoneError = "";
      return;
    }

    if (!this.telInputObj.telInput.value) {
      this.phoneValid = true; // Empty is okay for tertiary
      this.phoneError = "";
      return;
    }

    // Get the dial code from the lead object (set by ng2TelInput)
    const dialCode = '+'+ this.telInputObj.getSelectedCountryData().dialCode || "+20"; // Default to Egypt

    // Use the new validation function
    const validationResult = validatePhoneByType(
      this.telInputObj.telInput.value,
      dialCode,
      this.phoneType
    );

    if (!validationResult.isValid) {
      this.phoneValid = false;
      this.phoneError =
        validationResult.errorMessage || "Invalid phone number";
      // Update detected type even on invalid for user feedback
      if (this.phoneType === "auto" && validationResult.phoneType) {
        this.detectedPhoneType = validationResult.phoneType;
      }
    } else {
      this.phoneValid = true;
      this.phoneError = "";
      // Auto-detect and update the phone type if in auto mode
      if (this.phoneType === "auto" && validationResult.phoneType) {
        this.detectedPhoneType = validationResult.phoneType;
      }
    }
  }

  sendPhone() {
    // Guard check to ensure telInputObj is initialized
    if (!this.telInputObj || !this.telInputObj.telInput) {
      this.phoneEvent.emit({
        phone: '',
        code: '',
        phoneError: ''
      });
      return;
    }

    this.phoneEvent.emit({
      phone: this.telInputObj.telInput.value,
      code: '+' + this.telInputObj.getSelectedCountryData().dialCode,
      phoneError: this.phoneError
    });
  }

  ngOnDestroy() {
    // Cleanup telInput instance to prevent memory leaks
    if (this.telInputObj && this.telInputObj.destroy) {
      this.telInputObj.destroy();
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/iti-tel-input/iti-tel-input.component.ts